/**
 * Product API Service
 *
 * @package Enterprise\BookingSystem\Services
 */

/**
 * Internal dependencies
 */
import { BaseApiService } from "./base-api.service";

export type Fee = {
	amount: number;
	description?: string;
	formatted: string;
	is_active: boolean;
	name: string;
	type: string;
};

export type Package = {
	description: string;
	duration_hours: number;
	features: string[];
	formattedPrice: string;
	id: string;
	is_active: boolean;
	is_best_seller: boolean;
	name: string;
	price: number;
	sort_order: number;
};

export type Product = {
	description: string;
	fees: Fee[];
	formattedPrice: string;
	id: number;
	is_active: boolean;
	name: string;
	packages: Package[];
	price: number;
};

export type ProductVariant = {
	color: null | string;
	description: null | string;
	duration_hours: number;
	features: VariantFeature[];
	formatted_price: string;
	id: number;
	is_active: boolean;
	is_best_seller: boolean;
	is_default: boolean;
	name: string;
	price_amount: number;
	price_currency: string;
	product_id: number;
	sku: null | string;
	sort_order: number;
};

export type VariantFeature = {
	category: null | string;
	description: null | string;
	icon: null | string;
	id: number;
	is_highlighted: boolean;
	name: string;
	sort_order: number;
};

export class ProductApiService extends BaseApiService {
	/**
	 * Get product by ID
	 */
	async getProduct(productId: number): Promise<Product> {
		return this.get<Product>(`/products/${productId}`);
	}

	/**
	 * Get packages for a product
	 */
	async getPackages(productId: number): Promise<Package[]> {
		const response = await this.get<{ packages: Package[] }>(
			`/products/${productId}/packages`,
		);
		return response.packages;
	}

	/**
	 * Get variants for a product
	 */
	async getProductVariants(productId: number): Promise<ProductVariant[]> {
		const response = await this.get<ProductVariant[]>(
			`/products/${productId}/variants`,
		);
		return response;
	}

	/**
	 * Get all products
	 */
	async getProducts(): Promise<Product[]> {
		return this.get<Product[]>("/products");
	}
}

// Singleton instance
export const productApiService = new ProductApiService();
